/**
 * Boxes: blocks placed on a slideshow slide like on a magazine page.
 *
 * A stored spread is a bare [left, right] array, and many readers rely on
 * that, so a slide's boxes live on its lead entry: the left side of a spread,
 * or the single image. A bare id turns into an object once it has boxes.
 *
 * Positions and sizes are percent of the whole sheet (both pages of a spread),
 * so a box can cross the gutter. Boxes always draw above the pictures; among
 * themselves, later boxes draw on top.
 */

import { BLOCK_TYPES, createBlock } from '../blocks/schema.js';

export const BOX_BLOCK_TYPES = Object.freeze([
    BLOCK_TYPES.RICHTEXT,
    BLOCK_TYPES.COLUMNS,
    BLOCK_TYPES.VECTOR,
    BLOCK_TYPES.EMBED,
]);

/** What "Add a block" offers on a slide. */
export const BOX_PALETTE = Object.freeze([
    { type: BLOCK_TYPES.RICHTEXT, label: 'Text', hint: 'Words, headings and links' },
    { type: BLOCK_TYPES.COLUMNS, label: 'Columns', hint: 'Texts side by side' },
    { type: BLOCK_TYPES.VECTOR, label: 'Vector', hint: 'Brand SVG, tinted or original colours' },
    { type: BLOCK_TYPES.EMBED, label: 'Embed', hint: 'A YouTube or Vimeo link' },
]);

export const BOX_LIMITS = Object.freeze({
    count: 12,
    min: -50,
    max: 150,
    minSize: 3,
    maxSize: 200,
    maxPad: 120,
});

/** How a box shows on a phone: its own reading slide, drawn on its page, or not at all. */
export const BOX_PHONE = Object.freeze(['slide', 'page', 'hidden']);
export const BOX_VALIGN = Object.freeze(['top', 'center', 'bottom']);
/** Screens a box can be hidden on, see screenOf. */
export const BOX_SCREENS = Object.freeze(['desktop', 'tall', 'tablet', 'phone']);

/** Width of the sheet box contents are laid out at, then scaled to the real sheet. */
export const BOX_REFERENCE_WIDTH = 1200;

const PAPER_PREFIX = 'paper-';
const BOX_ID = /^[A-Za-z0-9_-]{1,64}$/;
const COLOUR = /^(#[0-9a-fA-F]{3,8}|rgba?\([0-9.,\s%]+\)|transparent)$/;

let counter = 0;
const stamp = () => {
    counter += 1;
    return `${Date.now().toString(36)}-${counter}`;
};

/** Pictures move and scale; these keep their shape, so only the width is set. */
export const keepsAspect = (type) => type === BLOCK_TYPES.VECTOR || type === BLOCK_TYPES.EMBED;

export const phoneDefault = (type) => (keepsAspect(type) ? 'page' : 'slide');

const isObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const round = (value) => Math.round(value * 100) / 100;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const finite = (value) => typeof value === 'number' && Number.isFinite(value);

/** A blank page: a slide, or one side of a spread, with no picture. */
export function isPaper(entry) {
    return isObject(entry) && entry.paper === true && typeof entry.id === 'string' && entry.id.startsWith(PAPER_PREFIX);
}

export function createPaper() {
    return { id: `${PAPER_PREFIX}${Date.now().toString(36)}${(counter += 1).toString(36)}`, paper: true };
}

/** A spread of two blank pages, for text alone. */
export function createBlankSpread() {
    return [createPaper(), createPaper()];
}

/** The id an entry (bare id, object, or one side of a spread) stands for. */
export function entryId(entry) {
    if (typeof entry === 'string') return entry;
    return isObject(entry) && typeof entry.id === 'string' ? entry.id : null;
}

/**
 * A new box holding a new block of `type`, `w` percent of the sheet wide and
 * centred on (cx, cy).
 */
export function createBox(type, { cx = 50, cy = 50, w = 40 } = {}) {
    let block = createBlock(type);
    if (type === BLOCK_TYPES.RICHTEXT) block = { ...block, maxWidth: null };
    if (type === BLOCK_TYPES.VECTOR) block = { ...block, width: { value: 100, unit: '%' } };
    const width = clamp(w, BOX_LIMITS.minSize, BOX_LIMITS.maxSize);
    return {
        id: `box-${stamp()}`,
        x: round(clamp(cx - width / 2, BOX_LIMITS.min, BOX_LIMITS.max)),
        y: round(clamp(cy - (keepsAspect(type) ? width / 3 : 5), BOX_LIMITS.min, BOX_LIMITS.max)),
        w: round(width),
        h: null,
        phone: phoneDefault(type),
        block,
    };
}

/**
 * The list with a new box of `type`, 40% of a page wide and centred on page
 * `side` (the whole sheet for a single picture). Unchanged when full.
 */
export function addBox(boxes, type, { single = true, pageWidth = 50, side = 0 } = {}) {
    const list = Array.isArray(boxes) ? boxes : [];
    if (list.length >= BOX_LIMITS.count || !BOX_BLOCK_TYPES.includes(type)) return list;
    const page = single ? 100 : pageWidth;
    const cx = !single && side === 1 ? 100 - page / 2 : page / 2;
    return [...list, createBox(type, { cx, cy: 50, w: page * 0.4 })];
}

/** The page of a slide new blocks go on: a blank page beside a picture, else the first. */
export function boxSide(entry) {
    return Array.isArray(entry) && !isPaper(entry[0]) && isPaper(entry[1]) ? 1 : 0;
}

/** The boxes on a slide's lead entry (or on a spread, through its left side). */
export function boxesOf(entry) {
    if (Array.isArray(entry)) return boxesOf(entry[0]);
    return isObject(entry) && Array.isArray(entry.boxes) ? entry.boxes : [];
}

/** The entry with these boxes; a side left with only its id goes back to a bare id. */
export function withBoxes(entry, boxes) {
    if (Array.isArray(entry)) return [withBoxes(entry[0], boxes), entry[1]];
    const list = Array.isArray(boxes) ? boxes : [];
    if (typeof entry === 'string') return list.length ? { id: entry, boxes: list } : entry;
    if (!isObject(entry)) return entry;
    const { boxes: _old, ...rest } = entry;
    if (list.length) return { ...rest, boxes: list };
    return Object.keys(rest).length === 1 && typeof rest.id === 'string' ? rest.id : rest;
}

function cleanNumber(value, min, max, fallback) {
    return finite(value) ? round(clamp(value, min, max)) : fallback;
}

/** One stored box made safe to render, or null when it can't be. */
export function normalizeBox(raw) {
    if (!isObject(raw) || !isObject(raw.block)) return null;
    const { block } = raw;
    if (!BOX_BLOCK_TYPES.includes(block.type) || typeof block.id !== 'string' || !block.id) return null;
    const id = typeof raw.id === 'string' && BOX_ID.test(raw.id) ? raw.id : `box-${block.id}`.slice(0, 64);
    const box = {
        id,
        x: cleanNumber(raw.x, BOX_LIMITS.min, BOX_LIMITS.max, 10),
        y: cleanNumber(raw.y, BOX_LIMITS.min, BOX_LIMITS.max, 10),
        w: cleanNumber(raw.w, BOX_LIMITS.minSize, BOX_LIMITS.maxSize, 40),
        h: keepsAspect(block.type) ? null : cleanNumber(raw.h, BOX_LIMITS.minSize, BOX_LIMITS.maxSize, null),
        phone: BOX_PHONE.includes(raw.phone) ? raw.phone : phoneDefault(block.type),
        block,
    };
    if (typeof raw.bg === 'string' && COLOUR.test(raw.bg.trim())) box.bg = raw.bg.trim();
    if (finite(raw.pad) && raw.pad > 0) box.pad = Math.round(clamp(raw.pad, 0, BOX_LIMITS.maxPad));
    if (box.h !== null && BOX_VALIGN.includes(raw.valign) && raw.valign !== 'top') box.valign = raw.valign;
    const hideOn = cleanHideOn(raw.hideOn);
    if (hideOn.length) box.hideOn = hideOn;
    return box;
}

function cleanHideOn(list) {
    return Array.isArray(list) ? BOX_SCREENS.filter((screen) => list.includes(screen)) : [];
}

/**
 * The screen a slideshow visitor has, from resolveExperience: a wide desktop
 * window, a tall or narrow one with a mouse, a tablet, or a phone.
 */
export function screenOf(experience) {
    if (!experience) return 'desktop';
    if (experience.phone) return 'phone';
    if (experience.touch) return 'tablet';
    return experience.shape === 'big-wide' ? 'desktop' : 'tall';
}

/** Whether a box shows on `screen`; the older phone: 'hidden' hides it on phones and tablets. */
export function boxShownOn(box, screen) {
    if (!box || !screen) return true;
    if (box.phone === 'hidden' && (screen === 'phone' || screen === 'tablet')) return false;
    return !(Array.isArray(box.hideOn) && box.hideOn.includes(screen));
}

/** A slide's boxes, cleaned: unusable ones dropped, ids unique, at most BOX_LIMITS.count. */
export function normalizeBoxes(list) {
    if (!Array.isArray(list)) return [];
    const seen = new Set();
    const out = [];
    list.forEach((raw) => {
        const box = normalizeBox(raw);
        if (!box || seen.has(box.id) || out.length >= BOX_LIMITS.count) return;
        seen.add(box.id);
        out.push(box);
    });
    return out;
}

/** Server check for a lead entry's boxes. */
export function validateBoxes(list) {
    if (list === undefined) return { valid: true };
    if (!Array.isArray(list)) return { valid: false, error: 'Boxes must be a list' };
    if (list.length > BOX_LIMITS.count) {
        return { valid: false, error: `A slide can hold at most ${BOX_LIMITS.count} blocks` };
    }
    const ids = new Set();
    for (const box of list) {
        if (!isObject(box)) return { valid: false, error: 'Each box must be an object' };
        if (typeof box.id !== 'string' || !BOX_ID.test(box.id)) return { valid: false, error: 'Each box needs an id' };
        if (ids.has(box.id)) return { valid: false, error: `Box ${box.id} is listed twice` };
        ids.add(box.id);
        if (!isObject(box.block) || typeof box.block.id !== 'string' || !box.block.id) {
            return { valid: false, error: `Box ${box.id} has no block` };
        }
        if (!BOX_BLOCK_TYPES.includes(box.block.type)) {
            return { valid: false, error: `Box ${box.id} can't hold a ${box.block.type} block` };
        }
        for (const key of ['x', 'y']) {
            if (!finite(box[key]) || box[key] < BOX_LIMITS.min || box[key] > BOX_LIMITS.max) {
                return { valid: false, error: `Box ${box.id} ${key} must be a number from ${BOX_LIMITS.min} to ${BOX_LIMITS.max}` };
            }
        }
        if (!finite(box.w) || box.w < BOX_LIMITS.minSize || box.w > BOX_LIMITS.maxSize) {
            return { valid: false, error: `Box ${box.id} width must be from ${BOX_LIMITS.minSize} to ${BOX_LIMITS.maxSize}` };
        }
        if (box.h !== null && box.h !== undefined
            && (!finite(box.h) || box.h < BOX_LIMITS.minSize || box.h > BOX_LIMITS.maxSize)) {
            return { valid: false, error: `Box ${box.id} height must be from ${BOX_LIMITS.minSize} to ${BOX_LIMITS.maxSize}` };
        }
        if (box.phone !== undefined && !BOX_PHONE.includes(box.phone)) {
            return { valid: false, error: `Box ${box.id} phone must be one of: ${BOX_PHONE.join(', ')}` };
        }
        if (box.bg !== undefined && !(typeof box.bg === 'string' && COLOUR.test(box.bg.trim()))) {
            return { valid: false, error: `Box ${box.id} has an invalid background` };
        }
        if (box.pad !== undefined && !(finite(box.pad) && box.pad >= 0 && box.pad <= BOX_LIMITS.maxPad)) {
            return { valid: false, error: `Box ${box.id} spacing must be from 0 to ${BOX_LIMITS.maxPad}` };
        }
        if (box.valign !== undefined && !BOX_VALIGN.includes(box.valign)) {
            return { valid: false, error: `Box ${box.id} has an invalid vertical position` };
        }
        if (box.hideOn !== undefined && !(Array.isArray(box.hideOn) && box.hideOn.every((screen) => BOX_SCREENS.includes(screen)))) {
            return { valid: false, error: `Box ${box.id} can only be hidden on: ${BOX_SCREENS.join(', ')}` };
        }
    }
    return { valid: true };
}

function findInBlock(block, id) {
    if (!block) return null;
    if (block.id === id) return block;
    if (block.type === BLOCK_TYPES.COLUMNS && Array.isArray(block.cells)) {
        for (const cell of block.cells) {
            const found = findInBlock(cell, id);
            if (found) return found;
        }
    }
    return null;
}

/** The box holding block `blockId` (the box's block or a columns cell), and where its slide is. */
export function findBox(images, blockId) {
    if (!Array.isArray(images) || !blockId) return null;
    for (let index = 0; index < images.length; index += 1) {
        const boxes = boxesOf(images[index]);
        for (const box of boxes) {
            const block = findInBlock(box.block, blockId);
            if (block) return { index, box, block };
        }
    }
    return null;
}

export function findBoxBlock(images, blockId) {
    return findBox(images, blockId)?.block || null;
}

/** The slide (index into images) a box sits on, by box id. */
export function findBoxById(images, boxId) {
    if (!Array.isArray(images) || !boxId) return null;
    for (let index = 0; index < images.length; index += 1) {
        const box = boxesOf(images[index]).find((entry) => entry.id === boxId);
        if (box) return { index, box };
    }
    return null;
}

/**
 * A slide's boxes as a phone shows them, one page at a time: `pages[i]` holds
 * the boxes kept on page i (in that page's own percent), `reading` the ones
 * that move to a reading slide after it. Hidden boxes are left out.
 * A box belongs to the page that holds its centre.
 */
export function phoneBoxes(boxes, { spread = false, screen = 'phone' } = {}) {
    const list = normalizeBoxes(boxes);
    const pages = spread ? [[], []] : [[]];
    const reading = [];
    list.forEach((box) => {
        if (box.phone === 'hidden' || !boxShownOn(box, screen)) return;
        if (box.phone === 'slide') {
            reading.push(box);
            return;
        }
        if (!spread) {
            pages[0].push(box);
            return;
        }
        const side = box.x + box.w / 2 < 50 ? 0 : 1;
        pages[side].push({ ...box, x: round((box.x - side * 50) * 2), w: round(box.w * 2) });
    });
    // Reading order: top to bottom, then left to right.
    reading.sort((a, b) => (a.y - b.y) || (a.x - b.x));
    return { pages, reading };
}

/** The slide (index into images) showing picture `imageId` on either side; -1 when none. */
export function slideIndexOf(images, imageId) {
    if (!Array.isArray(images) || !imageId) return -1;
    return images.findIndex((entry) => (Array.isArray(entry)
        ? entry.some((side) => entryId(side) === imageId)
        : entryId(entry) === imageId));
}

/** images with `fn(boxes)` applied to slide `index`'s boxes; the same array when nothing changes. */
export function updateSlideBoxes(images, index, fn) {
    if (!Array.isArray(images) || !images[index]) return images;
    const before = boxesOf(images[index]);
    const after = fn(before);
    if (after === before) return images;
    const next = [...images];
    next[index] = withBoxes(images[index], after);
    return next;
}

/** images with one box (by box id) replaced by `fn(box)`. */
export function patchBox(images, boxId, fn) {
    const found = findBoxById(images, boxId);
    if (!found) return images;
    return updateSlideBoxes(images, found.index, (boxes) => boxes.map((box) => (box.id === boxId ? fn(box) : box)));
}

function patchInBlock(block, id, fn) {
    if (block.id === id) return fn(block);
    if (block.type === BLOCK_TYPES.COLUMNS && Array.isArray(block.cells)) {
        let changed = false;
        const cells = block.cells.map((cell) => {
            const next = patchInBlock(cell, id, fn);
            if (next !== cell) changed = true;
            return next;
        });
        return changed ? { ...block, cells } : block;
    }
    return block;
}

/** images with block `blockId` (in a box, or a columns cell in one) replaced by `fn(block)`. */
export function patchBoxBlock(images, blockId, fn) {
    const found = findBox(images, blockId);
    if (!found) return images;
    return updateSlideBoxes(images, found.index, (boxes) => {
        let changed = false;
        const next = boxes.map((box) => {
            const block = patchInBlock(box.block, blockId, fn);
            if (block === box.block) return box;
            changed = true;
            return { ...box, block };
        });
        return changed ? next : boxes;
    });
}

/** A spread with its sides swapped: the boxes stay where they are on the sheet, on the new lead. */
export function swapSpreadSides(spread) {
    if (!Array.isArray(spread) || spread.length !== 2) return spread;
    const boxes = boxesOf(spread);
    return [withBoxes(spread[1], boxes), withBoxes(spread[0], [])];
}

/** The lead entry of two slides joined into a spread holds both slides' boxes. */
export function mergeBoxes(...entries) {
    const all = entries.flatMap((entry) => boxesOf(entry));
    const seen = new Set();
    return all.filter((box) => {
        if (seen.has(box.id)) return false;
        seen.add(box.id);
        return true;
    }).slice(0, BOX_LIMITS.count);
}

/** Library files one slide's boxes use (vector art and embed posters), so they aren't counted unused. */
export function boxFileIds(entry) {
    const ids = [];
    const visit = (block) => {
        if (!block) return;
        if (block.type === BLOCK_TYPES.VECTOR && typeof block.fileId === 'string' && block.fileId) ids.push(block.fileId);
        if (block.type === BLOCK_TYPES.EMBED && typeof block.posterFileId === 'string' && block.posterFileId) {
            ids.push(block.posterFileId);
        }
        if (block.type === BLOCK_TYPES.COLUMNS && Array.isArray(block.cells)) block.cells.forEach(visit);
    };
    boxesOf(entry).forEach((box) => visit(box.block));
    return ids;
}

export default {
    BOX_BLOCK_TYPES,
    BOX_PALETTE,
    BOX_LIMITS,
    BOX_PHONE,
    BOX_VALIGN,
    BOX_SCREENS,
    BOX_REFERENCE_WIDTH,
    screenOf,
    boxShownOn,
    createBlankSpread,
    keepsAspect,
    phoneDefault,
    isPaper,
    createPaper,
    entryId,
    createBox,
    addBox,
    boxSide,
    boxesOf,
    withBoxes,
    normalizeBox,
    normalizeBoxes,
    validateBoxes,
    findBox,
    findBoxBlock,
    findBoxById,
    phoneBoxes,
    slideIndexOf,
    updateSlideBoxes,
    patchBox,
    patchBoxBlock,
    swapSpreadSides,
    mergeBoxes,
    boxFileIds,
};
